/**
 * 블록 객체 팩토리 — 순수(부수효과 없음). editor 마운트·붙여넣기·업로드가 공유한다.
 * 저장 JSON(BlockNote 호환) 형태를 생성만 하며 DOM 을 만들지 않는다.
 */

/** 문단/리스트 기본 props(색·정렬) */
export const DEFAULT_BLOCK_PROPS = () => ({
  textColor: 'default',
  backgroundColor: 'default',
  textAlignment: 'left',
});

/** 빈 문단 n개(최소 1개) */
export function emptyParas(n) {
  return Array.from({ length: Math.max(1, n) }, () => ({
    type: 'paragraph',
    props: DEFAULT_BLOCK_PROPS(),
    content: [],
    children: [],
  }));
}

/**
 * initialContent 정규화 — 배열 또는 JSON 문자열 허용. 없거나 파싱 실패 시
 * initialEmptyBlocks(기본 3)개의 빈 문단으로 대체.
 */
export function resolveInitialContent(opts) {
  let raw = opts.initialContent;
  if (typeof raw === 'string') {
    try {
      raw = JSON.parse(raw);
    } catch (_) {
      raw = null;
    }
  }
  const n = opts.initialEmptyBlocks != null ? opts.initialEmptyBlocks : 3;
  if (raw == null) return emptyParas(n);
  return Array.isArray(raw) ? raw : emptyParas(n);
}

/**
 * 업로드된 미디어 블록 객체 생성(kingdoo-editor 데이터 구조와 동일).
 * - video: blocknote 커스텀 VideoBlock props {url}
 * - audio/file: blocknote 기본 파일블록(content 키 없음)
 * - image: blocknote 기본 image
 */
export function buildMediaBlock(type, url, name) {
  const n = name || '';
  if (type === 'video') return { type: 'video', props: { url }, children: [] };
  if (type === 'audio')
    return {
      type: 'audio',
      props: {
        backgroundColor: 'default',
        name: n,
        url,
        caption: '',
        showPreview: true,
      },
      children: [],
    };
  if (type === 'file')
    return {
      type: 'file',
      props: { backgroundColor: 'default', name: n, url, caption: '' },
      children: [],
    };
  return {
    type: 'image',
    props: {
      backgroundColor: 'default',
      textAlignment: 'left',
      name: n,
      url,
      caption: '',
      showPreview: true,
    },
    children: [],
  };
}

/** rows×cols 빈 표 블록(열폭 미지정) */
export function emptyTable(rows, cols) {
  rows = rows || 2;
  cols = cols || 2;
  const mkCell = () => ({
    type: 'tableCell',
    props: { colspan: 1, rowspan: 1 },
    content: [],
  });
  const rws = [];
  for (let r = 0; r < rows; r++) {
    const cells = [];
    for (let c = 0; c < cols; c++) cells.push(mkCell());
    rws.push({ cells });
  }
  return {
    type: 'table',
    props: {},
    content: {
      type: 'tableContent',
      columnWidths: new Array(cols).fill(null),
      rows: rws,
    },
    children: [],
  };
}

/** 2단 컬럼 블록(각 단에 빈 문단). n>2 는 2로 클램프(2단까지만 지원) */
export function emptyColumns(n, showDivider) {
  n = Math.min(2, n || 2);
  const cols = [];
  for (let i = 0; i < n; i++)
    cols.push({
      type: 'column',
      props: { width: 1 },
      children: [
        {
          type: 'paragraph',
          props: DEFAULT_BLOCK_PROPS(),
          content: [],
          children: [],
        },
      ],
    });
  return {
    type: 'columnList',
    props: showDivider ? { showDivider: true } : {},
    children: cols,
  };
}
